import type { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { UserGroupRole } from '@/shared/constants/userGroupRole';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

import type { EnhancedUser } from './EditAutocomplete';
import type { CompatibleGroup, CompatibleGroupUser, SwapModalData } from './EditPage.types';

interface HandleAddUsersOptions {
  groupId: number | undefined;
  isManagingUsers: boolean;
  group: CompatibleGroup | null | undefined;
  onAddUsers: (groupId: number, userIds: string[], group: CompatibleGroup | null) => Promise<unknown>;
  snackbar: SnackbarContextType;
}

export function createHandleAddUsers({ groupId, isManagingUsers, group, onAddUsers, snackbar }: HandleAddUsersOptions) {
  return async (selectedEmails: EnhancedUser[]) => {
    if (!groupId || selectedEmails.length === 0 || isManagingUsers) return;

    try {
      const userEmails = selectedEmails.map((s) => s.email).filter((email): email is string => typeof email === 'string');

      const result = await onAddUsers(groupId, userEmails, group ?? null);

      if (result) {
        snackbar.showSuccess('Users added successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  };
}

interface HandleRemoveUserOptions {
  groupId: number | undefined;
  isManagingUsers: boolean;
  totalCount: number;
  onRemoveSingleUser: (groupId: number, user: CompatibleGroupUser) => Promise<unknown>;
  ensureValidPageAfterRemove: (newTotal: number) => void;
  setRowSelection: (value: Record<string, never>) => void;
  snackbar: SnackbarContextType;
}

export function createHandleRemoveUser({
  groupId,
  isManagingUsers,
  totalCount,
  onRemoveSingleUser,
  ensureValidPageAfterRemove,
  setRowSelection,
  snackbar,
}: HandleRemoveUserOptions) {
  return async (user: CompatibleGroupUser) => {
    if (!groupId || !user.userId || isManagingUsers || !totalCount) return;

    try {
      const result = await onRemoveSingleUser(groupId, user);

      if (result) {
        const newTotal = totalCount - 1;
        ensureValidPageAfterRemove(newTotal);
        snackbar.showSuccess('User removed successfully');
        setRowSelection({});
      }
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  };
}
interface HandleRemoveSelectedUsersOptions {
  groupId: number | undefined;
  isManagingUsers: boolean;
  tableData: CompatibleGroupUser[];
  totalCount: number;
  rowSelection: Record<string, boolean>;
  onRemoveUsersInBatch: (groupId: number, users: CompatibleGroupUser[]) => Promise<boolean>;
  ensureValidPageAfterRemove: (newTotal: number) => void;
  setRowSelection: (value: Record<string, never>) => void;
  snackbar: SnackbarContextType;
}

export function createHandleRemoveSelectedUsers({
  groupId,
  isManagingUsers,
  tableData,
  totalCount,
  rowSelection,
  onRemoveUsersInBatch,
  ensureValidPageAfterRemove,
  setRowSelection,
  snackbar,
}: HandleRemoveSelectedUsersOptions) {
  return async () => {
    if (!groupId || isManagingUsers) return;

    const usersToRemove = tableData.filter((u) => u.userId && rowSelection[u.userId]);

    if (usersToRemove.length === 0) return;

    try {
      const success = await onRemoveUsersInBatch(groupId, usersToRemove);

      if (success) {
        const removedCount = usersToRemove.length;
        const newTotal = (totalCount ?? removedCount) - removedCount;
        setRowSelection({});
        ensureValidPageAfterRemove(newTotal);
        snackbar.showSuccess('Selected users removed successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  };
}

interface HandleRoleChangeOptions {
  groupId: number | undefined;
  isManagingUsers: boolean;
  organizationId: number;
  tableData: CompatibleGroupUser[];
  onUpdateRole: ((groupId: number, userId: string, newRole: string) => Promise<unknown>) | undefined;
  getDistributorGroupConflict:
    | ((
        organizationId: number,
        userId: string,
      ) => Promise<{ systemId?: number | null; distributorName?: string | null } | null | undefined>)
    | undefined;
  getBudgetGroupConflict:
    | ((organizationId: number, userId: string) => Promise<{ id?: number | null; name?: string | null } | null | undefined>)
    | undefined;
  setSwapModalData: (data: SwapModalData) => void;
  setSwapModalOpen: (open: boolean) => void;
  snackbar: SnackbarContextType;
}

export function createHandleRoleChange({
  groupId,
  isManagingUsers,
  organizationId,
  tableData,
  onUpdateRole,
  getDistributorGroupConflict,
  getBudgetGroupConflict,
  setSwapModalData,
  setSwapModalOpen,
  snackbar,
}: HandleRoleChangeOptions) {
  return async (userId: string, newRole: string) => {
    if (!groupId || !userId || isManagingUsers || !onUpdateRole) return;
    try {
      if (newRole === UserGroupRole.Distributor) {
        const userEmail = tableData.find((u) => u.userId === userId)?.email || userId;

        if (getDistributorGroupConflict) {
          const info = await getDistributorGroupConflict(organizationId, userId);
          if (info?.systemId && info.systemId !== groupId) {
            setSwapModalData({
              userId,
              userEmail,
              currentGroupId: info.systemId,
              currentGroupName: info.distributorName || `Distributor Group ${info.systemId}`,
              currentGroupType: 'distributor',
              newGroupId: 0,
              newGroupName: '',
            });
            setSwapModalOpen(true);
            return;
          }
        }

        if (getBudgetGroupConflict) {
          const info = await getBudgetGroupConflict(organizationId, userId);
          if (info?.id && info.id !== groupId) {
            setSwapModalData({
              userId,
              userEmail,
              currentGroupId: info.id,
              currentGroupName: info.name || info.id.toString(),
              currentGroupType: 'budget',
              newGroupId: 0,
              newGroupName: '',
            });
            setSwapModalOpen(true);
            return;
          }
        }
      }

      await onUpdateRole(groupId, userId, newRole);
      snackbar.showSuccess('User role updated successfully');
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  };
}

interface HandleSwapConfirmOptions {
  groupId: number | undefined;
  swapModalData: SwapModalData | null;
  onSwapConfirm: ((groupId: number, swapModalData: SwapModalData) => Promise<unknown>) | undefined;
  setSwapModalOpen: (open: boolean) => void;
  setSwapModalData: (data: SwapModalData | null) => void;
  snackbar: SnackbarContextType;
}

export function createHandleSwapConfirm({
  groupId,
  swapModalData,
  onSwapConfirm,
  setSwapModalOpen,
  setSwapModalData,
  snackbar,
}: HandleSwapConfirmOptions) {
  return async () => {
    if (!swapModalData || !groupId || !onSwapConfirm) return;
    try {
      const result = await onSwapConfirm(groupId, swapModalData);
      if (result) {
        snackbar.showSuccess('Distributor role changed successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
    setSwapModalOpen(false);
    setSwapModalData(null);
  };
}
